import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import {
  AlertIcon,
  Button,
  DateHolidaysPiker,
  H3,
  Separator,
  Toggle,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';

import { SettingsLock } from 'components';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { ESettingsAliases, SettingsContext } from 'settings';

import s from './HolidayTariffs.module.scss';

export const HolidayTariffs: FC = () => {
  const {
    state: {
      settings: { holidayTariffsEnabled, holidayTariffsDates },
      isLoading,
    },
    actions: { setSetting },
  } = useContext(SettingsContext);
  const { t } = useContext(LocalizationContext);
  const isAvailable = useIsFeatureAvailable('holiday_tariffs');

  const [dates, setDates] = useState<Set<string>>(new Set());

  useEffect(() => {
    setDates(new Set(holidayTariffsDates));
  }, [holidayTariffsDates]);

  const addDates = useCallback(
    (newDates: Set<string>) => {
      const newSet = new Set(
        Array.from(newDates).map((date) => dayjs(date).format('MM.DD'))
      );

      const sortedDates = Array.from(newSet).sort((a, b) =>
        dayjs(a, 'MM.DD').isAfter(dayjs(b, 'MM.DD')) ? 1 : -1
      );

      setSetting({
        alias: ESettingsAliases.HOLIDAY_TARIFFS_DATES,
        value: JSON.stringify(sortedDates),
      });
    },
    [holidayTariffsDates, dates]
  );

  const deleteDate = useCallback(
    (date: string) => {
      const newSet = new Set(dates);
      newSet.delete(date);

      const sortedDate = Array.from(newSet).sort((a, b) =>
        dayjs(a).isAfter(dayjs(b)) ? 1 : -1
      );

      setSetting({
        alias: ESettingsAliases.HOLIDAY_TARIFFS_DATES,
        value: JSON.stringify(sortedDate),
      });
    },
    [dates, holidayTariffsDates]
  );

  return (
    <>
      <div className={s.field}>
        <Toggle
          label={t('setting.holiday')}
          description={t('settings.billing.tariffs.holidays.description')}
          value={holidayTariffsEnabled}
          onChange={(value: boolean) =>
            setSetting({
              alias: ESettingsAliases.HOLIDAY_TARIFFS_ENABLED,
              value: value ? '1' : '0',
            })
          }
          variant="secondary"
          emptyBg
          disabled={!isAvailable}
          rightContent={!isAvailable ? <SettingsLock /> : undefined}
          className={s.toggle}
        />
        <div className={s.helper}>
          <AlertIcon size="s" />
          {t('setting.holiday.hint')}
        </div>

        <Separator className={s.separator} />

        <div className={s.holiday}>
          <div className={s.holiday__header}>
            <div className={s.header_block}>
              <H3>{t('setting.holiday.title')}</H3>
              <span className={s.holiday_text}>
                {t('settings.billing.tariffs.holidays.description_2')}
              </span>
            </div>
            <div
              className={cn(
                s.holiday_picker,
                (!holidayTariffsEnabled || !isAvailable) &&
                  s.holiday_picker_disabled
              )}
            >
              <DateHolidaysPiker
                btnText={t('add')}
                date={dates}
                setDate={(value) => {
                  addDates(value);
                }}
                disabled={!holidayTariffsEnabled || !isAvailable}
                modalButtonText={t('apply')}
              />
            </div>
          </div>
          {dates.size > 0 && holidayTariffsEnabled && (
            <div className={s.holiday__body}>
              {Array.from(dates).map((date) => (
                <div key={date} className={s.dates}>
                  <span>{dayjs(date).format('DD MMMM')}</span>
                  <Button
                    iconName="BucketIcon"
                    onClick={() => deleteDate(date)}
                    onlyIcon
                    variant="ghost"
                    disabled={isLoading}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
